/**
 * ShareWithAttorneyModal Component
 * 
 * Modal for sharing documents with attorneys
 */

import React, { useState } from 'react';
import { Document, DocumentShare } from '../types/documents';
import { X, Share2, Shield, AlertTriangle, Check, User, Calendar } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { format } from 'date-fns';

// ============================================================================
// Types
// ============================================================================

export interface ShareWithAttorneyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onShare: (documentIds: string[], notes?: string) => void;
  onRevoke: (documentId: string) => void;
  documents: Document[];
  existingShares?: DocumentShare[];
  isLoading?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export const ShareWithAttorneyModal: React.FC<ShareWithAttorneyModalProps> = ({
  isOpen,
  onClose,
  onShare,
  onRevoke,
  documents,
  existingShares = [],
  isLoading = false,
}) => {
  const [notes, setNotes] = useState('');
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  // Separate documents by share status
  const sharedDocs = documents.filter(d => d.is_shared_with_attorney);
  const unsharedDocs = documents.filter(d => !d.is_shared_with_attorney);

  const handleShare = () => {
    const idsToShare = unsharedDocs.map(d => d.id);
    onShare(idsToShare, notes.trim() || undefined);
  };

  const handleRevoke = (documentId: string) => {
    onRevoke(documentId);
    setConfirmRevoke(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-navy/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 overflow-hidden max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-sage/10 flex items-center justify-center">
              <Share2 className="w-5 h-5 text-sage" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-navy">
                Share with Attorney
              </h2>
              <p className="text-sm text-warm-gray">
                {documents.length} document{documents.length !== 1 ? 's' : ''} selected
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-cream transition-colors"
          >
            <X className="w-5 h-5 text-warm-gray" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Security Notice */}
          <div className="flex items-start gap-3 p-4 bg-sage/5 rounded-lg border border-sage/20">
            <Shield className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-navy mb-1">Secure Document Sharing</p>
              <p className="text-warm-gray">
                Shared documents are accessible only to attorneys linked to your family's legal case.
                You can revoke access at any time.
              </p>
            </div>
          </div>

          {/* Already Shared Documents */}
          {sharedDocs.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-navy mb-3 flex items-center gap-2">
                <Check className="w-4 h-4 text-sage" />
                Already Shared ({sharedDocs.length})
              </h3>
              <div className="space-y-2">
                {sharedDocs.map(doc => (
                  <div 
                    key={doc.id}
                    className="flex items-center justify-between p-3 bg-cream rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-navy truncate">{doc.title}</p>
                      {doc.shared_at && (
                        <p className="text-xs text-warm-gray flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Shared {format(new Date(doc.shared_at), 'MMM d, yyyy')}
                        </p>
                      )}
                    </div>
                    {confirmRevoke === doc.id ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleRevoke(doc.id)}
                          className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setConfirmRevoke(null)}
                          className="px-3 py-1 text-sm text-warm-gray hover:text-navy"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmRevoke(doc.id)}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Revoke Access
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Documents to Share */}
          {unsharedDocs.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-navy mb-3">
                Documents to Share ({unsharedDocs.length})
              </h3>
              <div className="space-y-2">
                {unsharedDocs.map(doc => (
                  <div 
                    key={doc.id}
                    className="flex items-center gap-3 p-3 bg-cream rounded-lg"
                  >
                    <div className="w-8 h-8 rounded bg-white flex items-center justify-center">
                      <Share2 className="w-4 h-4 text-warm-gray" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-navy truncate">{doc.title}</p>
                      <p className="text-xs text-warm-gray">
                        {doc.original_filename}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Notes */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-navy mb-1.5">
                  Notes for Attorney (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any relevant context about these documents..."
                  rows={3}
                  className={cn(
                    'w-full px-4 py-2.5 rounded-lg border border-gray-200 transition-colors',
                    'focus:outline-none focus:ring-2 focus:ring-sage/20 focus:border-sage',
                    'resize-none'
                  )}
                />
              </div>
            </div>
          )}

          {/* All Already Shared */}
          {unsharedDocs.length === 0 && sharedDocs.length > 0 && (
            <div className="text-center py-4">
              <p className="text-warm-gray">
                All selected documents are already shared with your attorney.
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-warm-gray hover:text-navy transition-colors"
          >
            {unsharedDocs.length === 0 ? 'Close' : 'Cancel'}
          </button>
          {unsharedDocs.length > 0 && (
            <button
              onClick={handleShare}
              disabled={isLoading}
              className={cn(
                'px-6 py-2 rounded-lg font-medium transition-all',
                'bg-sage text-white hover:bg-sage-dark',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'flex items-center gap-2'
              )}
            >
              <Share2 className="w-4 h-4" />
              {isLoading ? 'Sharing...' : `Share ${unsharedDocs.length} Document${unsharedDocs.length !== 1 ? 's' : ''}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareWithAttorneyModal;
